import {
  Box,
  Divider,
  FilledInput,
  FormControl,
  FormGroup,
  FormLabel,
  Grid,
  makeStyles,
  MenuItem,
  Select,
  Button,
  TextField,
} from "@material-ui/core";
import {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import AuthContext from "../../context";
import { del, get, post, put } from "../../utils/api-services";
import { getWeekNumber } from "../utils/getWeekNumber";
import Crud from "../../common/crud";
import { Autocomplete } from "@material-ui/lab";

const useStyles = makeStyles(() => ({
  root: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    paddingBottom: 20,
  },
  requiredValue: {
    fontWeight: "600",
    color: "rgba(255,0,0,1)",
  },
  buttonsContainer: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "flex-end",
    marginBottom: 20,
  },
}));

const PerimeterForm = forwardRef(
  ({ idfinca_plan_type, onSave, selectedRow, setSelectedRow, hideSections, cultivations }, ref) => {
    const { showToast, showLoader } = useContext(AuthContext);
    const classes = useStyles();
    const [selectedFinca, setSelectedFinca] = useState(null);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [invalidData, setInvalidData] = useState(false);
    const [canStore, setCanStore] = useState(true);
    const [editingAddedProductIndex, setEditingAddedProductIndex] = useState(null);
    const [allCultivationTypes, setAllCultivationTypes] = useState([]);
    const [allSections, setAllSections] = useState([]);
    const [state, setState] = useState({
      fincas: [],
      products: [],
      employees: [],
      measures: [],
      cultivations: [],
      productsList: [],
      perimeter: {
        date: "",
        start_date: "",
        end_date: "",
        reentry_date: "",
        reentry_time: "",
        finca: "",
        week: "",
        acres: "",
        product: "",
        productMeasure: "",
        idfinca_measure: null,
        product_application_mode: "",
        productType: "",
        dose_per_acre: null,
        dose: null,
        tanks_applied: null,
        unit_cost: "",
        total_cost: "",
        total_cost_per_acre: "",
        total_product_cost_per_acre: "",
        EPA: "",
        ingredients: "",
        sprayer: "",
        productApplicationModeOptions: [],
        sections: [],
      },
    });

    const sedeName = (JSON.parse(localStorage.getItem("@sede"))?.name || "").toLowerCase();
    const containsDeland = sedeName.includes("deland");
    const containsOregon = sedeName.includes("oregon");
    const containsFlorida =
      sedeName.includes("florida") ||
      sedeName.includes("deland") ||
      sedeName.includes("miami");
    const selectedFarmByPerimeter = state.fincas.find(
      (f) => String(f.idfinca_farm) === String(state.perimeter.finca)
    );
    const isOregonSectionFlow =
      containsOregon && !hideSections && !cultivations;
    const isNonOregonCultivationFlow = !containsOregon && cultivations;
    const isFarmOnlyPerimeterFlow =
      !containsOregon && Number(idfinca_plan_type) === 1 && !hideSections && !cultivations;

    const initialPerimeterState = {
      date: "",
      start_date: "",
      end_date: "",
      reentry_date: "",
      reentry_time: "",
      finca: "",
      acres: "",
      product: "",
      productMeasure: "",
      idfinca_measure: null,
      product_application_mode: "",
      productType: "",
      dose_per_acre: null,
      dose: null,
      tanks_applied: null,
      unit_cost: "",
      total_cost: "",
      total_cost_per_acre: "",
      total_product_cost_per_acre: "",
      EPA: "",
      ingredients: "",
      sprayer: "",
      productApplicationModeOptions: [],
      sections: [],
    };

    const toNumber = (value) =>
      value === "" || value === null || value === undefined ? 0 : Number(value);
    const format4 = (value) => (Number.isFinite(value) ? value.toFixed(4) : "0.0000");

    const normalizeProductRecord = (product = {}) => {
      const { EPA, epa_number, ...rest } = product;
      return {
        ...rest,
        EPA: product.EPA ?? product.epa_number ?? "",
      };
    };

    const emptyProductFields = {
      product: "", productMeasure: "", idfinca_measure: null, product_application_mode: "",
      productType: "", dose_per_acre: null, dose: null, tanks_applied: null,
      unit_cost: "", total_cost: "", total_cost_per_acre: "", total_product_cost_per_acre: "",
      EPA: "", ingredients: "", sprayer: "",
      productApplicationModeOptions: [],
    };

    const hasAreaSelection = (areaValue) =>
      Array.isArray(areaValue) ? areaValue.length > 0 : Boolean(areaValue);

    const baseCanStore = (currentPerimeter) => {
      if (isFarmOnlyPerimeterFlow) {
        return (
          Boolean(currentPerimeter.finca) &&
          Boolean(currentPerimeter.sections?.length) &&
          Number(currentPerimeter.acres) > 0
        );
      }

      return (
        (hideSections
          ? hasAreaSelection(currentPerimeter.area)
          : Boolean(currentPerimeter.sections?.length)) &&
        (isOregonSectionFlow ? true : hasAreaSelection(currentPerimeter.area)) &&
        Number(currentPerimeter.acres) > 0
      );
    };

    const buildProductFromPerimeter = (currentPerimeter) => ({
      idfinca_product: currentPerimeter.product,
      product_name:
        state.products.find((productItem) => productItem.idfinca_product === currentPerimeter.product)?.name ||
        "",
      idfinca_measure:
        currentPerimeter.idfinca_measure ??
        state.measures.find((measureItem) => measureItem.unit === currentPerimeter.productMeasure)
          ?.idfinca_measure ??
        state.products.find((productItem) => productItem.idfinca_product === currentPerimeter.product)
          ?.fincasMeasure?.idfinca_measure ??
        null,
      productMeasure: currentPerimeter.productMeasure,
      dose_per_acre: currentPerimeter.dose_per_acre,
      dose: currentPerimeter.dose,
      tanks_applied: currentPerimeter.tanks_applied,
      unit_cost: currentPerimeter.unit_cost,
      total_cost: currentPerimeter.total_cost,
      total_cost_per_acre: currentPerimeter.total_cost_per_acre,
      total_product_cost_per_acre: currentPerimeter.total_product_cost_per_acre,
      idfinca_application_mode: currentPerimeter.product_application_mode,
      EPA: currentPerimeter.EPA || "",
      ingredients: currentPerimeter.ingredients,
      sprayer: currentPerimeter.sprayer,
      product_application_mode_options: currentPerimeter.productApplicationModeOptions || [],
    });

    const normalizeProductPayload = (productItem) => ({
      ...normalizeProductRecord(productItem),
    });

    const getProductsForSubmission = (currentPerimeter = state.perimeter) => {
      if (state.productsList.length > 0) return state.productsList.map(normalizeProductPayload);
      if (currentPerimeter.product) return [normalizeProductPayload(buildProductFromPerimeter(currentPerimeter))];
      return [];
    };

    const hasPendingProductDraft = () => {
      const p = state.perimeter;
      return Boolean(p.product || p.productMeasure || p.product_application_mode || p.dose_per_acre || p.dose || p.tanks_applied || p.unit_cost);
    };

    const getFarmSections = (farmId, fincasSource = state.fincas, sectionsSource = allSections) => {
      if (!farmId) return [];

      const sectionsFromFarms =
        fincasSource
          .find((farm) => String(farm.idfinca_farm) === String(farmId))
          ?.areas.flatMap((area) =>
            (area.sections || []).map((section) => ({
              ...section,
              idfinca_area: section.idfinca_area ?? area.idfinca_area ?? null,
            }))
          ) || [];

      const sectionsFromEndpoint = (sectionsSource || [])
        .filter((section) => {
          const sectionFarmId =
            section.idfinca_farm ??
            section.farm?.idfinca_farm ??
            section.area?.idfinca_farm ??
            section.area?.farm?.idfinca_farm;
          return String(sectionFarmId) === String(farmId);
        })
        .map((section) => ({
          ...section,
          idfinca_area:
            section.idfinca_area ??
            section.area?.idfinca_area ??
            null,
        }));

      const merged = [...sectionsFromFarms, ...sectionsFromEndpoint];
      const unique = new Map();
      merged.forEach((section) => {
        if (section?.idfinca_section != null) {
          unique.set(String(section.idfinca_section), section);
        }
      });

      return Array.from(unique.values());
    };

    const getSectionDistribution = () => {
      if (hideSections) {
        const selectedAreaIds = (Array.isArray(state.perimeter.area) ? state.perimeter.area : [state.perimeter.area])
          .filter(Boolean)
          .map((id) => String(id));
        const areas = selectedFarmByPerimeter?.areas.filter((area) =>
          selectedAreaIds.includes(String(area.idfinca_area))
        ) || [];
        const total = areas.reduce((sum, area) => sum + (Number(area.acres) || 0), 0);

        return areas.map((area) => ({
          idfinca_section: null,
          idfinca_area: area.idfinca_area,
          acres: Number(area.acres) || 0,
          ratio: total > 0 ? (Number(area.acres) || 0) / total : 0,
        }));
      }

      const selected = state.perimeter.sections || [];
      const pool = getFarmSections(state.perimeter.finca);
      const selectedIds = selected.map((id) => String(id));
      const sections = pool.filter((s) => selectedIds.includes(String(s.idfinca_section)));
      const total = sections.reduce((sum, s) => sum + (Number(s.acres) || 0), 0);
      return sections.map((s) => ({
        idfinca_section: s.idfinca_section,
        idfinca_area: s.idfinca_area,
        acres: Number(s.acres) || 0,
        ratio: total > 0 ? (Number(s.acres) || 0) / total : 0,
      }));
    };

    const hasSectionsWithoutArea = (sectionDistribution = []) =>
      sectionDistribution.some((s) => !s.idfinca_area);

    const buildDistributedDetail = (productItem, sectionInfo) => {
      const usesDoseAsDosePerAcre = !containsDeland && !containsOregon;
      const distributedDose = (containsDeland || cultivations)
        ? productItem.dose
        : format4(toNumber(productItem.dose) * sectionInfo.ratio);
      const distributedTanks = containsDeland ? productItem.tanks_applied : format4(toNumber(productItem.tanks_applied) * sectionInfo.ratio);
      const distributedDosePerAcre = containsDeland
        ? format4(toNumber(productItem.dose_per_acre) * sectionInfo.ratio)
        : (usesDoseAsDosePerAcre ? productItem.dose : productItem.dose_per_acre);
      const detailTotalCost = format4(toNumber(productItem.unit_cost) * toNumber(distributedDose));
      const detailTotalCostPerAcre = isNonOregonCultivationFlow
        ? format4(toNumber(productItem.unit_cost) * toNumber(sectionInfo.acres) * toNumber(distributedDose))
        : format4(toNumber(productItem.unit_cost) * toNumber(sectionInfo.acres) * toNumber(distributedDosePerAcre));
      const detailTotalProductCostPerAcre = format4(
        toNumber(productItem.dose) * toNumber(productItem.tanks_applied) * toNumber(productItem.unit_cost) * sectionInfo.ratio
      );
      return { distributedDose, distributedTanks, distributedDosePerAcre, detailTotalCost, detailTotalCostPerAcre, detailTotalProductCostPerAcre };
    };

    const buildStorePlanPayload = (productItem, sectionInfo) => {
      const distributedDetail = buildDistributedDetail(productItem, sectionInfo);
      const sede = JSON.parse(localStorage.getItem("@sede") || "{}");
      const isOregonSede = sede?.name?.toLowerCase?.().includes("oregon") || false;
      const sprayer = productItem.sprayer || state.perimeter.sprayer || "";
      const floridaOnlyEPA = containsFlorida ? productItem.EPA ?? "" : "";
      const floridaOnlyIngredients = containsFlorida ? productItem.ingredients ?? "" : "";
      const floridaOnlySprayer = containsFlorida ? sprayer : "";
      
      const sectionId = sectionInfo.idfinca_section ?? null;
      return {
        idfinca_plan_type,
        idfinca_area: isOregonSede ? null : (sectionInfo.idfinca_area ?? state.perimeter.area),
        ...(sectionId != null ? { idfinca_section: sectionId } : {}),
        idfinca_farm: state.perimeter.finca,
        date: state.perimeter.date,
        start_date: containsFlorida ? state.perimeter.start_date : "",
        end_date: containsFlorida ? state.perimeter.end_date : "",
        reentry_date: containsFlorida ? state.perimeter.reentry_date : "",
        reentry_time: containsFlorida ? state.perimeter.reentry_time : "",
        EPA: floridaOnlyEPA,
        EPA_number: floridaOnlyEPA,
        ingredients: floridaOnlyIngredients,
        sprayer: floridaOnlySprayer,
        week: getWeekNumber(state.perimeter.date),
        total_acres: sectionInfo.acres ?? state.perimeter.acres,
        total_cost: distributedDetail.detailTotalCost,
        detail: {
          idfinca_product: productItem.idfinca_product,
          idfinca_measure: productItem.idfinca_measure,
          dose_per_acre: distributedDetail.distributedDosePerAcre,
          dose: distributedDetail.distributedDose,
          tanks_applied: distributedDetail.distributedTanks,
          unit_cost: productItem.unit_cost,
          total_cost: distributedDetail.detailTotalCost,
          total_cost_per_acre: distributedDetail.detailTotalCostPerAcre,
          total_product_cost_per_acre: distributedDetail.detailTotalProductCostPerAcre,
          idfinca_application_mode: productItem.idfinca_application_mode,
          EPA: floridaOnlyEPA,
          EPA_number: floridaOnlyEPA,
          ingredients: floridaOnlyIngredients,
          sprayer: floridaOnlySprayer,
        },
      };
    };

    const submitProductsBySection = async (productsForSubmission, sectionDistribution) => {
      let successCount = 0, failCount = 0;
      for (const productItem of productsForSubmission) {
        for (const sectionInfo of sectionDistribution) {
          const res = await post("/fincas/planDetails/item/storePlanWithDetail", buildStorePlanPayload(productItem, sectionInfo));
          if (res.status === 200) {
            successCount++;
            const cacheKey = `${state.perimeter.finca}_${state.perimeter.date}`;
            const overlay = JSON.parse(localStorage.getItem("@fincas_plan_extra") || "{}");
            overlay[cacheKey] = {
              start_date: containsFlorida ? state.perimeter.start_date || "" : "",
              end_date: containsFlorida ? state.perimeter.end_date || "" : "",
              reentry_date: containsFlorida ? state.perimeter.reentry_date || "" : "",
              reentry_time: containsFlorida ? state.perimeter.reentry_time || "" : "",
              sprayer: containsFlorida ? productItem.sprayer || state.perimeter.sprayer || "" : "",
            };
            localStorage.setItem("@fincas_plan_extra", JSON.stringify(overlay));
          } else {
            failCount++;
          }
        }
      }
      return { successCount, failCount };
    };

    const handleAddCurrentProduct = () => {
      if (!state.perimeter.product) {
        showToast("Please select a product before adding it.", "error");
        return;
      }

      if (
        state.perimeter.productApplicationModeOptions?.length > 0 &&
        !state.perimeter.product_application_mode
      ) {
        showToast("Application Mode is required.", "error");
        return;
      }

      if (!containsDeland) {
        if (!state.perimeter.dose) {
          showToast("Dose is required.", "error");
          return;
        }
        if (containsOregon && !state.perimeter.tanks_applied) {
          showToast("Tanks Applied is required.", "error");
          return;
        }
      } else if (!state.perimeter.dose_per_acre) {
        showToast("Dose Per Acre is required.", "error");
        return;
      }

      const productToAdd = buildProductFromPerimeter(state.perimeter);

      setState((prev) => ({
        ...prev,
        productsList:
          editingAddedProductIndex !== null
            ? prev.productsList.map((item, idx) =>
              idx === editingAddedProductIndex ? productToAdd : item
            )
            : [...prev.productsList, productToAdd],
        perimeter: { ...prev.perimeter, ...emptyProductFields },
      }));

      setSelectedProduct(null);
      setEditingAddedProductIndex(null);
      setCanStore(baseCanStore(state.perimeter));
      showToast(
        editingAddedProductIndex !== null
          ? "Product updated in the plan"
          : "Product added to the plan",
        "success"
      );
    };

    const handleEditAddedProduct = (indexToEdit) => {
      const item = state.productsList[indexToEdit];
      if (!item) return;

      const productObj = state.products.find(
        (p) => p.idfinca_product === item.idfinca_product
      );

      const modeOptions =
        productObj?.fincasApplicationModes?.map((mode) => ({
          id: mode.idfinca_application_mode,
          name: mode.name,
        })) || item.product_application_mode_options || [];

      setState((prev) => ({
        ...prev,
        perimeter: {
          ...prev.perimeter,
          product: item.idfinca_product,
          productMeasure:
            item.productMeasure || productObj?.fincasMeasure?.unit || "",
          idfinca_measure:
            item.idfinca_measure ||
            productObj?.fincasMeasure?.idfinca_measure ||
            null,
          product_application_mode: item.idfinca_application_mode || "",
          productType: productObj?.fincasProductType?.name || "",
          EPA: item.EPA || productObj?.EPA || "",
          ingredients: item.ingredients || productObj?.ingredients || "",
          dose_per_acre: item.dose_per_acre,
          dose: item.dose,
          tanks_applied: item.tanks_applied,
          unit_cost: item.unit_cost,
          total_cost: item.total_cost,
          total_cost_per_acre: item.total_cost_per_acre,
          total_product_cost_per_acre: item.total_product_cost_per_acre,
          productApplicationModeOptions: modeOptions,
        },
      }));

      setSelectedProduct(productObj || null);
      setEditingAddedProductIndex(indexToEdit);
    };

    const clearCurrentProductFields = () => {
      setState((prev) => ({ ...prev, perimeter: { ...prev.perimeter, ...emptyProductFields } }));
      setSelectedProduct(null);
      setEditingAddedProductIndex(null);
    };

    const removeAddedProduct = (indexToRemove) => {
      setState((prev) => {
        const nextList = prev.productsList.filter((_, i) => i !== indexToRemove);
        setCanStore(
          baseCanStore(prev.perimeter) &&
          (nextList.length > 0 ||
            (Boolean(prev.perimeter.product) &&
              toNumber(prev.perimeter.unit_cost) !== 0))
        );
        return {
          ...prev,
          productsList: nextList,
        };
      });

      if (editingAddedProductIndex === indexToRemove) {
        clearCurrentProductFields();
      } else if (
        editingAddedProductIndex !== null &&
        indexToRemove < editingAddedProductIndex
      ) {
        setEditingAddedProductIndex((prev) =>
          prev === null ? prev : prev - 1
        );
      }
    };

    const selectedFarmObj = state.fincas.find(
        (f) => String(f.idfinca_farm) === String(selectedRow?.fincasPlan?.idfinca_farm)
      );
      const selectedSectionsFromRow = selectedRow?.fincasPlan?.sections 
        ? (Array.isArray(selectedRow.fincasPlan.sections) 
            ? selectedRow.fincasPlan.sections 
            : [selectedRow.fincasPlan.sections])
        : (selectedRow?.fincasPlan?.idfinca_section 
            ? [selectedRow.fincasPlan.idfinca_section] 
            : []);
        const selectedSectionIdsFromRow = selectedSectionsFromRow.map((id) => String(id));
      
      const selectedSectionObjs = selectedFarmObj?.areas.flatMap((area) => area.sections || [])
          .filter((sec) => selectedSectionIdsFromRow.includes(String(sec.idfinca_section))) || [];
        const selectedSectionAcres = selectedSectionObjs.reduce(
          (sum, sec) => sum + (Number(sec.acres) || 0),
          0
        );
      
      const autofilledCultivation = selectedSectionObjs[0]?.idfinca_cultivation_type || "";

    useEffect(() => {
      makeRequest();
      setEditingAddedProductIndex(null);
      setState((prev) => ({
        ...prev,
        products: Array.isArray(prev.products) ? prev.products.map(normalizeProductRecord) : prev.products,
        productsList:
          selectedRow?.idfinca_product || selectedRow?.product?.idfinca_product
            ? [
              {
                idfinca_product:
                  selectedRow?.idfinca_product ||
                  selectedRow?.product?.idfinca_product ||
                  "",
                product_name: selectedRow?.product?.name || "",
                idfinca_measure:
                  selectedRow?.detailMeasure?.idfinca_measure ||
                  selectedRow?.product?.fincasMeasure?.idfinca_measure ||
                  null,
                productMeasure:
                  selectedRow?.detailMeasure?.unit ||
                  selectedRow?.product?.fincasMeasure?.unit ||
                  "",
                dose_per_acre: selectedRow?.dose_per_acre,
                dose: selectedRow?.dose,
                tanks_applied: selectedRow?.tanks_applied,
                unit_cost: selectedRow?.unit_cost,
                total_cost: selectedRow?.total_cost,
                total_cost_per_acre: selectedRow?.total_cost_per_acre,
                total_product_cost_per_acre:
                  selectedRow?.total_product_cost_per_acre,
                idfinca_application_mode:
                  selectedRow?.idfinca_application_mode || "",
                EPA:
                  selectedRow?.EPA ||
                  normalizeProductRecord(selectedRow?.product || {}).EPA ||
                  "",
                ingredients:
                  selectedRow?.ingredients ||
                  selectedRow?.product?.ingredients ||
                  "",
                product_application_mode_options:
                  selectedRow?.product?.fincasApplicationModes?.map((mode) => ({
                    id: mode.idfinca_application_mode,
                    name: mode.name,
                  })) || [],
              },
            ]
            : [],
        perimeter: {
          date: selectedRow?.fincasPlan?.date,
          start_date: containsFlorida ? (selectedRow?.start_date || selectedRow?.fincasPlan?.start_date || "") : "",
          end_date: containsFlorida ? (selectedRow?.end_date || selectedRow?.fincasPlan?.end_date || "") : "",
          reentry_date: containsFlorida ? (selectedRow?.reentry_date || selectedRow?.fincasPlan?.reentry_date || "") : "",
          reentry_time: containsFlorida ? (selectedRow?.reentry_time || selectedRow?.fincasPlan?.reentry_time || "") : "",
          week: selectedRow?.fincasPlan?.week || "",
          acres: selectedSectionAcres || selectedRow?.fincasPlan?.total_acres || "",
          area: hideSections
            ? (selectedRow?.fincasPlan?.idfinca_area ? [selectedRow.fincasPlan.idfinca_area] : [])
            : selectedRow?.fincasPlan?.idfinca_area,
          sections: selectedSectionsFromRow,
          product: Array.isArray(selectedRow?.product) && selectedRow.product.length > 0
              ? selectedRow.product[0].idfinca_product : selectedRow?.idfinca_product || "",
          cultivation: autofilledCultivation,
          finca: selectedRow?.fincasPlan?.idfinca_farm || "",
          productMeasure:
            selectedRow?.detailMeasure?.unit
            ?? selectedRow?.product?.fincasMeasure?.unit
            ?? "",
          idfinca_measure:
            selectedRow?.detailMeasure?.idfinca_measure
            || selectedRow?.product?.fincasMeasure?.idfinca_measure
            || null,
          product_application_mode: selectedRow?.idfinca_application_mode || "",
          productType: selectedRow?.product?.fincasProductType?.name || "",
          EPA: selectedRow?.EPA || normalizeProductRecord(selectedRow?.product || {}).EPA || "",
          ingredients: selectedRow?.ingredients || selectedRow?.product?.ingredients || "",
          sprayer: selectedRow?.sprayer || "",
          dose_per_acre: selectedRow?.dose_per_acre,
          dose: selectedRow?.dose,
          tanks_applied: selectedRow?.tanks_applied,
          unit_cost: selectedRow?.unit_cost,
          total_cost: selectedRow?.total_cost,
          total_cost_per_acre: selectedRow?.total_cost_per_acre,
          total_product_cost_per_acre: selectedRow?.total_product_cost_per_acre,
          productApplicationModeOptions: selectedRow?.product?.fincasApplicationModes.map((mode, i) => ({
            id: mode.idfinca_application_mode,
            name: mode.name,
          })) || [],
        },
      }));
    }, [selectedRow]);

    const makeRequest = async () => {
      showLoader(true);

      try {
        const [fincas, products, employees, measures, cultivationTypes, sections] = await Promise.all([
          get("/fincas/farms"),
          get("/fincas/products"),
          get("/employees"),
          get("/fincas/measures"),
          get("/fincas/cultivationTypes"),
          get("/fincas/sections"),
        ]);
        setAllSections(Array.isArray(sections?.data) ? sections.data : []);

        setState((prev) => ({
          ...prev,
          fincas: Array.isArray(fincas?.data) ? fincas.data : [],
          products: Array.isArray(products?.data) ? products.data.map(normalizeProductRecord) : [],
          employees: Array.isArray(employees?.data) ? employees.data : [],
          measures: Array.isArray(measures?.data) ? measures.data : [],
        }));

        setAllCultivationTypes(Array.isArray(cultivationTypes?.data) ? cultivationTypes.data : []);
      } catch (error) {
        showToast("Error fetching data:", "error");
      } finally {
        showLoader(false);
      }
    };

    const handleParameterChange = ({ target }) => {
      const val = target.name === "product_application_mode"
        ? Number(target.value)
        : target.value;

      const MEASURE_FACTOR_OLD_PER_NEW = {
        "lb->kg": 2.20462,
        "kg->lb": 0.45359237,
        "oz->gal": 128,
        "gal->oz": 1 / 128,
      };
      const oldPerNew = (oldU, newU) =>
        MEASURE_FACTOR_OLD_PER_NEW[`${oldU}->${newU}`] ?? 1;
      const toNum = (v) => (v === "" || v === null || v === undefined ? 0 : Number(v));
      const fmt4 = (n) => (Number.isFinite(n) ? n.toFixed(4) : "0.0000");

      setState((prev) => {
        const newState = {
          ...prev,
          perimeter: { ...prev.perimeter, [target.name]: val },
        };
        const calcFarmId = target.name === "finca" ? val : prev.perimeter.finca;
        const calcFarm = prev.fincas.find(
          (f) => String(f.idfinca_farm) === String(calcFarmId)
        );
        const isOregonSectionFlowCalc =
          containsOregon &&
          !hideSections &&
          !cultivations;

        if (target.name === "finca" && cultivations) {
          newState.perimeter.cultivation = "";
          newState.perimeter.acres = "";
          newState.perimeter.area = hideSections ? [] : "";
          newState.perimeter.sections = [];
          newState.perimeter.missingMessage = "";
          return newState;
        }

        if (target.name === "finca" && isFarmOnlyPerimeterFlow) {
          const selectedFarmObj = prev.fincas.find(
            (f) => String(f.idfinca_farm) === String(val)
          );
          const farmSections = selectedFarmObj
            ? selectedFarmObj.areas.flatMap((farmArea) =>
              (farmArea.sections || []).map((section) => ({
                ...section,
                idfinca_area: section.idfinca_area ?? farmArea.idfinca_area,
              }))
            )
            : [];

          newState.perimeter.area = "";
          newState.perimeter.sections = farmSections.map((section) => section.idfinca_section);
          newState.perimeter.acres = farmSections.reduce(
            (sum, section) => sum + (Number(section.acres) || 0),
            0
          );
        }
        let finca = selectedFinca;
        let product = selectedProduct;

        if (target.name === "sections" && !hideSections) {
          const selectedIds = (val || []).map((id) => String(id));
          const selectedSections = isOregonSectionFlowCalc
            ? getFarmSections(calcFarmId, prev.fincas, allSections).filter(
                (sec) => selectedIds.includes(String(sec.idfinca_section))
              )
            : prev.fincas
                .find((f) => f.idfinca_farm === prev.perimeter.finca)
                ?.areas.find((a) => a.idfinca_area === prev.perimeter.area)
                ?.sections.filter((sec) => selectedIds.includes(String(sec.idfinca_section))) || [];
          const totalAcres = selectedSections.reduce((sum, sec) => sum + (Number(sec.acres) || 0), 0);
          newState.perimeter.acres = totalAcres;
          if (isOregonSectionFlowCalc) {
            newState.perimeter.area =
              selectedSections.length > 0 ? selectedSections[0].idfinca_area : "";
          }
        }
        else if (target.name === "area" && hideSections) {
          const selectedIds = (Array.isArray(val) ? val : [val]).filter(Boolean).map((id) => String(id));
          const selectedAreas = prev.fincas.find(f => f.idfinca_farm === prev.perimeter.finca)
            ?.areas.filter(a => selectedIds.includes(String(a.idfinca_area))) || [];
          const sumAcres = selectedAreas.reduce((sum, area) => sum + (Number(area.acres) || 0), 0);
          newState.perimeter.acres = sumAcres;
          newState.perimeter.sections = [];
        }
        else if (target.name === "cultivation" && cultivations) {
          const farmSections = getFarmSections(prev.perimeter.finca, prev.fincas, allSections);
          const matchingSections = farmSections.filter(
            sec => String(sec.idfinca_cultivation_type) === String(val)
          );
          const sumAcres = matchingSections.reduce(
            (sum, sec) => sum + (Number(sec.acres) || 0), 0
          );
          newState.perimeter.area = matchingSections.length > 0 ? matchingSections[0].idfinca_area : "";
          newState.perimeter.sections = matchingSections.map(sec => sec.idfinca_section);
          newState.perimeter.acres = sumAcres;
        }
        else if (!(target.name === "finca" && isFarmOnlyPerimeterFlow)) {
          newState.perimeter.acres = finca?.acres || prev.perimeter.acres || 0;
        }
        if (target.name === "product") {
          product = prev.products.find(
            (p) => p.idfinca_product === target.value
          );
          setSelectedProduct(product);
          if (product) {
            const normalizedProduct = normalizeProductRecord(product);
            newState.perimeter.productMeasure = product.fincasMeasure.unit;
            newState.perimeter.idfinca_measure = product.fincasMeasure.idfinca_measure;
            newState.perimeter.product_application_mode = "";
            newState.perimeter.productApplicationModeOptions = product.fincasApplicationModes.map((mode, index) => ({
              id: mode.idfinca_application_mode,
              name: mode.name
            }))
            newState.perimeter.productType = product.fincasProductType.name;
            newState.perimeter.EPA = normalizedProduct.EPA;
            newState.perimeter.ingredients = product.ingredients || "";

            const costPerPresentation = Number(product.fincasProductSedes[0]?.cost_per_presentation ?? 0);

            const totalPresentation = Number(product.fincasProductSedes[0]?.total_presentation ?? 0);

            let unitCost = 0;

            if (totalPresentation > 0) {
              unitCost = parseFloat((costPerPresentation / totalPresentation).toFixed(4));
            }

            newState.perimeter.unit_cost = parseFloat(unitCost).toFixed(4);

            setCanStore(unitCost !== 0);

            if (unitCost === 0) {
              const errorMessage = 'Unit Cost cannot be 0';
              showToast(errorMessage, "error");
            }
          }
        }

        if (target.name === "productMeasure") {
          const prevUnit = prev.perimeter.productMeasure;
          const nextUnit = val;
          if (prevUnit && nextUnit && prevUnit !== nextUnit) {
            const found = (prev.measures || []).find((m) => m.unit === nextUnit);
            newState.perimeter.idfinca_measure = found ? found.idfinca_measure : null;

            const factor = oldPerNew(prevUnit, nextUnit);
            const unitCostOld = toNum(prev.perimeter.unit_cost);
            const unitCostNew = unitCostOld * factor;
            newState.perimeter.unit_cost = fmt4(unitCostNew);
          }
        }
        const unitCost = toNum(newState.perimeter.unit_cost);
        const doseFinal = toNum(
          target.name === "dose" ? target.value : newState.perimeter.dose
        );
        const dpaFinal = toNum(
          target.name === "dose_per_acre" ? target.value : newState.perimeter.dose_per_acre
        );
        const acresFinal = toNum(newState.perimeter.acres);
        const tanksFinal = toNum(
          target.name === "tanks_applied" ? target.value : newState.perimeter.tanks_applied
        );
        const effectiveDosePerAcre = !containsDeland && !containsOregon ? doseFinal : dpaFinal;

        newState.perimeter.total_cost = fmt4(unitCost * doseFinal || 0);
        newState.perimeter.total_cost_per_acre = fmt4(
          (isNonOregonCultivationFlow ? unitCost * acresFinal * doseFinal : unitCost * acresFinal * effectiveDosePerAcre) || 0
        );
        newState.perimeter.total_product_cost_per_acre = fmt4(
          toNum(newState.perimeter.total_cost) * tanksFinal || 0
        );

        let missingMessage = "";

        if (hideSections && hasAreaSelection(newState.perimeter.area)) {
          if (!(Number(newState.perimeter.acres) > 0)) {
            missingMessage = "Acres must be greater than 0.";
          }
        }

        if (cultivations && newState.perimeter.cultivation) {
          if (!Boolean(newState.perimeter.area)) {
            missingMessage = "The cultivation selected does not exist in the selected farm.";
          } else if (!Boolean(newState.perimeter.sections?.length)) {
            missingMessage = "The cultivation selected does not exist in the selected farm.";
          } else if (!(Number(newState.perimeter.acres) > 0)) {
            missingMessage = "Acres must be greater than 0 for the selected cultivation.";
          }
        }
        setCanStore(
          (isFarmOnlyPerimeterFlow
            ? Boolean(newState.perimeter.finca) && Boolean(newState.perimeter.sections?.length)
            : (hideSections
              ? hasAreaSelection(newState.perimeter.area)
              : Boolean(newState.perimeter.sections?.length))) &&
          (isOregonSectionFlowCalc || isFarmOnlyPerimeterFlow ? true : hasAreaSelection(newState.perimeter.area)) &&
          Number(newState.perimeter.acres) > 0 &&
          ((newState.productsList?.length > 0) || unitCost !== 0)
        );
        newState.perimeter.missingMessage = missingMessage;
        return newState;
      });
    };

    const validatePerimeter = (perimeter) => {
      const {
        finca,
        date,
        acres,
        area,
        sections,
        total_cost,
        product,
        dose_per_acre,
        dose,
        tanks_applied,
        unit_cost,
        total_cost_per_acre,
        total_product_cost_per_acre,
        product_application_mode,
        productApplicationModeOptions,
        cultivation,
      } = perimeter;

      const missingFields = [];
      const productsForSubmission = getProductsForSubmission(perimeter);
      if (!date || isNaN(new Date(date).getTime())) missingFields.push("Date");
      if (!finca) missingFields.push("Farm");
      if (!productsForSubmission.length) missingFields.push("Product");
      if (!hasAreaSelection(area) && !cultivations && !isOregonSectionFlow && !isFarmOnlyPerimeterFlow) missingFields.push("Area");
      if ((!sections || sections.length === 0) && !cultivations && !hideSections && !isFarmOnlyPerimeterFlow) missingFields.push("Sections");
      if ((!sections || sections.length === 0) && isFarmOnlyPerimeterFlow) missingFields.push("Sections for selected farm");
      if (cultivations && !cultivation) missingFields.push("Cultivations");

      productsForSubmission.forEach((productItem, index) => {
        const label = productsForSubmission.length > 1 ? ` (Product #${index + 1})` : "";
        if (
          productItem.product_application_mode_options?.length > 0 &&
          !productItem.idfinca_application_mode
        ) {
          missingFields.push(`Application Mode${label}`);
        }

        if (!containsDeland && !productItem.dose) {
          missingFields.push(`Dose${label}`);
        }
        if (!containsDeland && containsOregon && !productItem.tanks_applied) {
          missingFields.push(`Tanks Applied${label}`);
        }
        if (containsDeland && !productItem.dose_per_acre) {
          missingFields.push(`Dose Per Acre${label}`);
        }
      });

      if (missingFields.length > 0) {
        const errorMessage = `The following fields are required: ${missingFields.join(
          ", "
        )}.`;
        showToast(errorMessage, "error");
        setInvalidData(true);
        return false;
      }

      setInvalidData(false);
      return true;
    };

    const store = async () => {
      if (!validatePerimeter(state.perimeter)) return false;

      showLoader(true);
      
      try {
        const sectionDistribution = getSectionDistribution();
        const productsForSubmission = getProductsForSubmission();

        if (!isOregonSectionFlow && hasSectionsWithoutArea(sectionDistribution)) {
          showLoader(false);
          showToast("One or more selected sections are not linked to an area.", "error");
          return;
        }

        if (!productsForSubmission.length) {
          showLoader(false);
          showToast("Please add at least one product.", "error");
          return;
        }

        const { successCount, failCount } = await submitProductsBySection(
          productsForSubmission,
          sectionDistribution
        );

        showLoader(false);

        if (successCount > 0) {
          setState((prev) => ({
            ...prev,
            perimeter: { ...initialPerimeterState },
            productsList: [],
          }));
          
          if (failCount > 0) {
            showToast(`${successCount} records saved successfully, ${failCount} failed`, "warning");
          } else {
            showToast(`${successCount} record(s) saved successfully`);
          }
          onSave();
        } else {
          showToast("Failed to save records", "error");
        }
      } catch (error) {
        showLoader(false);
        console.error("Error storing data:", error);
        showToast(
          error.message || "An unexpected error occurred. Please try again.",
          "error"
        );
      }
    };

    const update = async () => {
      if (!selectedRow || Object.keys(selectedRow).length === 0) {
        showToast("No row selected. Please select a row to update.", "error");
        return;
      }

      if (!validatePerimeter(state.perimeter)) return;

      showLoader(true);

      try {
        const recordsToDelete = selectedRow.relatedRecords || [selectedRow];
        
        for (const record of recordsToDelete) {
          await del(`/fincas/plans/item/${record.idfinca_plan}`);
          await del(`/fincas/planDetails/item/${record.idfinca_plan_detail}`);
        }

        const sectionDistribution = getSectionDistribution();
        const productsForSubmission = getProductsForSubmission();

        if (!isOregonSectionFlow && hasSectionsWithoutArea(sectionDistribution)) {
          showLoader(false);
          showToast("One or more selected sections are not linked to an area.", "error");
          return;
        }

        if (!productsForSubmission.length) {
          showLoader(false);
          showToast("Please add at least one product.", "error");
          return;
        }

        const { successCount, failCount } = await submitProductsBySection(
          productsForSubmission,
          sectionDistribution
        );

        showLoader(false);

        if (successCount > 0) {
          if (failCount > 0) {
            showToast(`Updated: ${successCount} records created, ${failCount} failed`, "warning");
          } else {
            showToast(`Updated successfully: ${successCount} record(s)`, "success");
          }
          onSave();
          clearForm();
        } else {
          showToast("Failed to update records", "error");
        }
      } catch (error) {
        showLoader(false);
        console.error("Error updating data:", error);
        showToast(
          error.message || "An unexpected error occurred. Please try again.",
          "error"
        );
      }
    };

    const destroy = async () => {
      try {
        if (!selectedRow || Object.keys(selectedRow).length === 0) {
          showToast("No row selected. Please select a row to delete.", "error");
          return;
        }

        showLoader(true);
        const recordsToDelete = selectedRow.relatedRecords || [selectedRow];
        let successCount = 0;
        let failCount = 0;

        for (const record of recordsToDelete) {
          const url = `/fincas/plans/item/${record.idfinca_plan}`;
          const url2 = `/fincas/planDetails/item/${record.idfinca_plan_detail}`;

          const response = await del(url);
          const response2 = await del(url2);

          if (response.status === 200 || response2.status === 200) {
            successCount++;
          } else {
            failCount++;
          }
        }

        showLoader(false);

        if (successCount > 0) {
          showToast(`Deleted successfully: ${successCount} record(s)`, "success");
          onSave();
          clearForm();
        } else {
          showToast("Failed to delete records", "error");
        }
      } catch (error) {
        showLoader(false);
        console.error("Error deleting data:", error);
        showToast(
          error.message || "An unexpected error occurred. Please try again.",
          "error"
        );
      }
    };

    const clearForm = () => {
      setState((prev) => ({
        ...prev,
        perimeter: { ...initialPerimeterState },
        productsList: [],
      }));
      setSelectedFinca(null);
      setSelectedProduct(null);
      setEditingAddedProductIndex(null);
      setSelectedRow((prev) => ({
        ...prev,
        rowSelected: null,
      }));
    };

    useImperativeHandle(ref, () => ({
      clearForm,
    }));

    const getAllowedMeasureUnits = (unit) => {
      if (["lb", "kg"].includes(unit)) return ["lb", "kg"];
      if (["oz", "gal"].includes(unit)) return ["oz", "gal"];
      return [unit];
    };

    const measureOptions = (() => {
      const currentUnit = state.perimeter.productMeasure || "";
      const allowed = getAllowedMeasureUnits(currentUnit);
      return (state.measures || []).filter(m => allowed.includes(m.unit));
    })();

    const sectionOptions = state.perimeter.finca
      ? isOregonSectionFlow
        ? getFarmSections(state.perimeter.finca)
        : selectedFarmByPerimeter?.areas.find(
            (a) => a.idfinca_area === state.perimeter.area
          )?.sections || []
      : [];

    const areaOptions = state.perimeter.finca
      ? state.fincas.find((f) => f.idfinca_farm === state.perimeter.finca)?.areas || []
      : [];

    return (
      <div>
        <Grid container>
          <Grid item xs={12} sm={3} lg={2}>
            <FormGroup>
              <FormLabel>
                {"Date"} <span className={classes.requiredValue}>*</span>{" "}
              </FormLabel>
              <FormControl variant="filled" component={Box} width="100%">
                <FilledInput
                  autoComplete="off"
                  type="date"
                  placeholder="Date"
                  name="date"
                  value={state.perimeter.date}
                  onChange={handleParameterChange}
                  inputProps={{
                    max: new Date().toISOString().split("T")[0],
                  }}
                />
              </FormControl>
            </FormGroup>
          </Grid>
          {containsFlorida && (
            <>
              <Grid item xs={12} sm={3} lg={2}>
                <FormGroup>
                  <FormLabel>{"Start"}</FormLabel>
                  <FormControl variant="filled" component={Box} width="100%">
                    <FilledInput
                      autoComplete="off"
                      type="date"
                      placeholder="Start Date"
                      name="start_date"
                      value={state.perimeter.start_date}
                      onChange={handleParameterChange}
                      inputProps={{
                        max: new Date().toISOString().split("T")[0],
                      }}
                    />
                  </FormControl>
                </FormGroup>
              </Grid>
              <Grid item xs={12} sm={3} lg={2}>
                <FormGroup>
                  <FormLabel>{"End"}</FormLabel>
                  <FormControl variant="filled" component={Box} width="100%">
                    <FilledInput
                      autoComplete="off"
                      type="date"
                      placeholder="End Date"
                      name="end_date"
                      value={state.perimeter.end_date}
                      onChange={handleParameterChange}
                      inputProps={{
                        max: new Date().toISOString().split("T")[0],
                      }}
                    />
                  </FormControl>
                </FormGroup>
              </Grid>
              <Grid item xs={12} sm={3} lg={2}>
                <FormGroup>
                  <FormLabel>{"Re-Entry Date"}</FormLabel>
                  <FormControl variant="filled" component={Box} width="100%">
                    <FilledInput
                      autoComplete="off"
                      type="date"
                      placeholder="Re-Entry Date"
                      name="reentry_date"
                      value={state.perimeter.reentry_date}
                      onChange={handleParameterChange}
                      inputProps={{
                        max: new Date().toISOString().split("T")[0],
                      }}
                    />
                  </FormControl>
                </FormGroup>
              </Grid>
              <Grid item xs={12} sm={3} lg={2}>
                <FormGroup>
                  <FormLabel>{"Re-Entry Time"}</FormLabel>
                  <FormControl variant="filled" component={Box} width="100%">
                    <FilledInput
                      autoComplete="off"
                      type="time"
                      placeholder="Re-Entry Time"
                      name="reentry_time"
                      value={state.perimeter.reentry_time}
                      onChange={handleParameterChange}
                    />
                  </FormControl>
                </FormGroup>
              </Grid>
            </>
          )}
          <Grid item xs={12} sm={2} lg={2}>
            <FormGroup>
              <FormLabel>{"Week"}</FormLabel>
              <FormControl variant="filled" component={Box} width="100%">
                <FilledInput
                  autoComplete="off"
                  type="text"
                  name="code"
                  value={
                    state.perimeter.date
                      ? getWeekNumber(state.perimeter.date)
                      : ""
                  }
                  disabled
                />
              </FormControl>
            </FormGroup>
          </Grid>

          <Grid
            container
            style={{ paddingLeft: 15 }}
            alignItems="center"
            item
            xs={12}
          >
            {/* Farms */}
            <Grid item xs={12} sm={4} lg={4}>
              <FormGroup>
                <FormLabel>
                  Farms <span className={classes.requiredValue}>*</span>
                </FormLabel>
                <Autocomplete
                  options={state.fincas}
                  getOptionLabel={(option) => option.name || ""}
                  getOptionSelected={(option, value) =>
                    option.idfinca_farm === value.idfinca_farm
                  }
                  value={
                    state.perimeter.finca
                      ? state.fincas.find(
                        (farm) => farm.idfinca_farm === state.perimeter.finca
                      )
                      : null
                  }
                  onChange={(event, newValue) =>
                    handleParameterChange({
                      target: {
                        name: "finca",
                        value: newValue?.idfinca_farm || "",
                      },
                    })
                  }
                  renderInput={(params) => <TextField {...params} />}
                  ListboxProps={{
                    style: { maxHeight: "170px", overflow: "auto" },
                  }}
                  noOptionsText="No farms available"
                />
              </FormGroup>
            </Grid>

          {/* Cultivations */}
          {cultivations && state.perimeter.finca && (
            <Grid item xs={12} sm={4} lg={4}>
              <FormGroup>
                <FormLabel>
                  Cultivations <span className={classes.requiredValue}>*</span>
                </FormLabel>
                <Autocomplete
                  options={allCultivationTypes}
                  getOptionLabel={(option) => option?.name || ""}
                  getOptionSelected={(option, value) =>
                    option.idfinca_cultivation_type === value.idfinca_cultivation_type
                  }
                  value={
                    state.perimeter.cultivation
                      ? allCultivationTypes.find(
                          (cult) =>
                            cult.idfinca_cultivation_type === state.perimeter.cultivation
                        )
                      : null
                  }
                  onChange={(event, newValue) =>
                    handleParameterChange({
                      target: {
                        name: "cultivation",
                        value: newValue?.idfinca_cultivation_type || "",
                      },
                    })
                  }
                  renderInput={(params) => <TextField {...params} />}
                  ListboxProps={{
                    style: { maxHeight: "170px", overflow: "auto" },
                  }}
                  noOptionsText="No cultivations available"
                />
              </FormGroup>
            </Grid>
          )}

            {/* Areas */}
            {state.perimeter.finca && !cultivations && !isOregonSectionFlow && !isFarmOnlyPerimeterFlow && (
              <Grid item xs={12} sm={4} lg={4}>
                <FormGroup>
                  <FormLabel>
                    Areas <span className={classes.requiredValue}>*</span>
                  </FormLabel>
                  <Autocomplete
                    multiple={hideSections}
                    options={
                      areaOptions
                    }
                    getOptionLabel={(option) => option.name || ""}
                    getOptionSelected={(option, value) =>
                      option.idfinca_area === value.idfinca_area
                    }
                    value={
                      hideSections
                        ? areaOptions.filter((area) =>
                            (Array.isArray(state.perimeter.area) ? state.perimeter.area : [])
                              .map((id) => String(id))
                              .includes(String(area.idfinca_area))
                          )
                        : state.perimeter.area
                          ? areaOptions.find(
                              (area) => area.idfinca_area === state.perimeter.area
                            )
                          : null
                    }
                    onChange={(event, newValue) =>
                      handleParameterChange({
                        target: {
                          name: "area",
                          value: hideSections
                            ? (newValue || []).map((area) => area.idfinca_area)
                            : newValue?.idfinca_area || "",
                        },
                      })
                    }
                    renderInput={(params) => <TextField {...params} placeholder={hideSections ? "Select areas" : undefined} />}
                    ListboxProps={{
                      style: { maxHeight: "170px", overflow: "auto" },
                    }}
                    noOptionsText="No areas available"
                  />
                </FormGroup>
              </Grid>
            )}

            {/* Sections */}
            {(isOregonSectionFlow ? state.perimeter.finca : state.perimeter.area) && !hideSections && !cultivations && !isFarmOnlyPerimeterFlow && (
              <Grid item xs={12} sm={4} lg={4}>
                <FormGroup>
                  <FormLabel>
                    Sections <span className={classes.requiredValue}>*</span>
                  </FormLabel>
                  <Autocomplete
                    multiple
                    options={sectionOptions}
                    getOptionLabel={(option) => option.name || ""}
                    getOptionSelected={(option, value) =>
                      option.idfinca_section === value.idfinca_section
                    }
                    value={
                      state.perimeter.sections && state.perimeter.sections.length > 0
                        ? sectionOptions.filter((s) =>
                            state.perimeter.sections
                              .map((id) => String(id))
                              .includes(String(s.idfinca_section))
                          )
                        : []
                    }
                    onChange={(event, newValue) =>
                      handleParameterChange({
                        target: {
                          name: "sections",
                          value: newValue.map(v => v.idfinca_section),
                        },
                      })
                    }
                    renderInput={(params) => <TextField {...params} placeholder="Select sections" />}
                    ListboxProps={{
                      style: { maxHeight: "170px", overflow: "auto" },
                    }}
                  />
                </FormGroup>
              </Grid>
            )}

            {/* Acres - always to the right of Farms/Areas/Sections */}
            <Grid item xs={12} sm={4} lg={2}>
              <FormGroup>
                <FormLabel>{"Acres"}</FormLabel>
                <FormControl variant="filled" component={Box} width="100%">
                  <FilledInput
                    autoComplete="off"
                    type="text"
                    name="code"
                    value={state.perimeter.acres || ""}
                    disabled
                  />
                </FormControl>
              </FormGroup>
            </Grid>
          </Grid>

          {/* Products Section Divider */}
          <Grid item xs={12}>
            <Divider style={{ margin: "16px 0 8px 0" }} />
            <FormLabel style={{ fontWeight: 600, fontSize: "0.85rem", color: "#555", letterSpacing: 1, textTransform: "uppercase" }}>
              Products
            </FormLabel>
            <Divider style={{ margin: "8px 0 12px 0" }} />
          </Grid>

          <Grid
            item
            xs={12}
            sm={5}
            lg={6}
            sx={{ display: "flex", flexDirection: "column", gap: 1 }}
          >
            <FormGroup sx={{ flexGrow: 1 }}>
              <FormLabel sx={{ marginBottom: "4px" }}>
                {"Product"} <span className={classes.requiredValue}>*</span>{" "}
              </FormLabel>
              <Autocomplete
                options={state.products}
                getOptionLabel={(option) => option.name || ""}
                getOptionSelected={(option, value) =>
                  option.idfinca_product === value.idfinca_product
                }
                value={
                  state.perimeter.product
                    ? state.products.find(
                      (product) =>
                        product.idfinca_product === state.perimeter.product
                    )
                    : null
                }
                onChange={(event, newValue) =>
                  handleParameterChange({
                    target: {
                      name: "product",
                      value: newValue?.idfinca_product || "",
                    },
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    InputProps={{
                      ...params.InputProps,
                    }}
                  />
                )}
                ListboxProps={{
                  style: {
                    maxHeight: "170px",
                    overflow: "auto",
                  },
                }}
                noOptionsText="No products available"
              />
            </FormGroup>
          </Grid>
          <Grid item xs={12} sm={2} lg={2}>
            <FormGroup>
              <FormLabel>{"Measure"} <span className={classes.requiredValue}>*</span>{" "}</FormLabel>
              <FormControl variant="filled" component={Box} width="100%">
                <Select
                  name="productMeasure"
                  value={state.perimeter.productMeasure || ""}
                  onChange={handleParameterChange}
                  displayEmpty
                  disabled={!state.perimeter.product}
                >
                  <MenuItem value="" disabled>
                    <em>Select measure</em>
                  </MenuItem>
                  {measureOptions.map((m) => (
                    <MenuItem key={m.idfinca_measure} value={m.unit}>
                      {m.unit}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </FormGroup>
          </Grid>

          {state.perimeter.productApplicationModeOptions.length > 0 && (
            <Grid item xs={12} sm={3} lg={2}>
              <FormGroup>
                <FormLabel>{"Application Mode"} <span className={classes.requiredValue}>*</span>{" "}</FormLabel>
                <FormControl
                  variant="filled"
                  component={Box}
                  width="100%"
                >
                  <Select
                    name="product_application_mode"
                    value={state.perimeter.product_application_mode}
                    onChange={handleParameterChange}
                    displayEmpty
                  >
                    <MenuItem value="" disabled>
                      <em>Select Application Mode</em>
                    </MenuItem>
                    {state.perimeter.productApplicationModeOptions.map((opt) => (
                      <MenuItem key={opt.id} value={opt.id}>
                        {opt.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </FormGroup>
            </Grid>
          )}
          <Grid item xs={12} sm={2} lg={2}>
            <FormGroup>
              <FormLabel>{"Type"}</FormLabel>
              <FormControl variant="filled" component={Box} width="100%">
                <FilledInput
                  autoComplete="off"
                  type="text"
                  name="code"
                  value={state.perimeter.productType || ""}
                  disabled
                />
              </FormControl>
            </FormGroup>
          </Grid>
          {containsDeland ? (
            <>
              <Grid item xs={6} sm={3} lg={6}>
                <FormGroup>
                  <FormLabel>
                    {"Dose Per Acre"}{" "}
                    <span className={classes.requiredValue}>*</span>{" "}
                  </FormLabel>
                  <FormControl variant="filled" component={Box} width="100%">
                    <FilledInput
                      autoComplete="off"
                      type="text"
                      name="dose_per_acre"
                      value={state.perimeter.dose_per_acre || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value > 0 || value === "") {
                          handleParameterChange(e);
                        }
                      }}
                    />
                  </FormControl>
                </FormGroup>
              </Grid>
            </>
          ) : (
            <></>
          )}

          {!containsDeland ? (
            <>
              <Grid item xs={6} sm={3} lg={3}>
                <FormGroup>
                  <FormLabel>
                    {containsOregon ? "Dose" : "Dose/Acre"} <span className={classes.requiredValue}>*</span>{" "}
                  </FormLabel>
                  <FormControl variant="filled" component={Box} width="100%">
                    <FilledInput
                      autoComplete="off"
                      type="number"
                      name="dose"
                      value={state.perimeter.dose || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value > 0 || value === "") {
                          handleParameterChange(e);
                        }
                      }}
                    />
                  </FormControl>
                </FormGroup>
              </Grid>
          {!containsDeland && containsOregon ? (
            <>
              <Grid item xs={6} sm={3} lg={3}>
                <FormGroup>
                  <FormLabel>
                    {"Tanks Applied"}{" "}
                    <span className={classes.requiredValue}>*</span>
                  </FormLabel>
                  <FormControl variant="filled" component={Box} width="100%">
                    <FilledInput
                      autoComplete="off"
                      type="number"
                      name="tanks_applied"
                      value={state.perimeter.tanks_applied || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value > 0 || value === "") {
                          handleParameterChange(e);
                        }
                      }}
                    />
                  </FormControl>
                </FormGroup>
              </Grid>
            </>
          ) : (
            <></>
          )}
            </>
          ) : (
            <></>
          )}
          <Grid item xs={6} sm={3} lg={2}>
            <FormGroup>
              <FormLabel>{"Unit Cost"}</FormLabel>
              <FormControl variant="filled" component={Box} width="100%">
                <FilledInput
                  autoComplete="off"
                  type="text"
                  name="unit_cost"
                  value={state.perimeter.unit_cost ? `$${state.perimeter.unit_cost}` : ""}
                  disabled
                />
              </FormControl>
            </FormGroup>
          </Grid>
          {containsOregon && (
            <Grid item xs={6} sm={3} lg={2}>
              <FormGroup>
                <FormLabel>{"Total Product Cost/Acre"}</FormLabel>
                <FormControl variant="filled" component={Box} width="100%">
                  <FilledInput
                    autoComplete="off"
                    type="text"
                    name="total_product_cost_per_acre"
                    value={state.perimeter.total_product_cost_per_acre ? `$${state.perimeter.total_product_cost_per_acre}` : ""}
                    disabled
                  />
                </FormControl>
              </FormGroup>
            </Grid>
          )}
          {!containsDeland && !containsOregon ? (
            <>
              <Grid item xs={6} sm={3} lg={2}>
                <FormGroup>
                  <FormLabel>{"Total Cost"}</FormLabel>
                  <FormControl variant="filled" component={Box} width="100%">
                    <FilledInput
                      autoComplete="off"
                      type="text"
                      name="total_cost"
                      value={state.perimeter.total_cost ? `$${state.perimeter.total_cost}` : ""}
                      disabled
                    />
                  </FormControl>
                </FormGroup>
              </Grid>
            </>
          ) : (
            <></>
          )}

          {!containsOregon ? (
            <>
              <Grid item xs={6} sm={3} lg={2}>
                <FormGroup>
                  <FormLabel>{"Total Cost Per Acre"}</FormLabel>
                  <FormControl variant="filled" component={Box} width="100%">
                    <FilledInput
                      autoComplete="off"
                      type="text"
                      name="total_cost_per_acre"
                      value={state.perimeter.total_cost_per_acre ? `$${state.perimeter.total_cost_per_acre}` : ""}
                      disabled
                    />
                  </FormControl>
                </FormGroup>
              </Grid>
            </>
          ) : (
            <></>
          )}

          {!containsDeland && !containsOregon ? (
            <>
              <Grid item xs={6} sm={3} lg={2}>
                <FormGroup>
                  <FormLabel>{"Total Product Cost Per Acre"}</FormLabel>
                  <FormControl variant="filled" component={Box} width="100%">
                    <FilledInput
                      autoComplete="off"
                      type="text"
                      name="total_product_cost_per_acre"
                      value={state.perimeter.total_product_cost_per_acre ? `$${state.perimeter.total_product_cost_per_acre}` : ""}
                      disabled
                    />
                  </FormControl>
                </FormGroup>
              </Grid>
            </>
          ) : (
            <></>
          )}
          {containsFlorida && (
            <>
              <Grid item xs={12} sm={3} lg={2}>
                <FormGroup>
                  <FormLabel>{"EPA #"}</FormLabel>
                  <FormControl variant="filled" component={Box} width="100%">
                    <FilledInput
                      autoComplete="off"
                      type="text"
                      name="EPA"
                      value={state.perimeter.EPA || ""}
                      disabled
                    />
                  </FormControl>
                </FormGroup>
              </Grid>
              <Grid item xs={12} sm={3} lg={2}>
                <FormGroup>
                  <FormLabel>{"Ingredients"}</FormLabel>
                  <FormControl variant="filled" component={Box} width="100%">
                    <FilledInput
                      autoComplete="off"
                      type="text"
                      name="ingredients"
                      value={state.perimeter.ingredients || ""}
                      disabled
                    />
                  </FormControl>
                </FormGroup>
              </Grid>
              <Grid item xs={12} sm={3} lg={2}>
                <FormGroup>
                  <FormLabel>{"Sprayer"}</FormLabel>
                  <Autocomplete
                    options={state.employees}
                    value={state.employees.find((employee) => employee.name === state.perimeter.sprayer) || null}
                    getOptionLabel={(option) => option?.name ? `${option.code ? `${option.code} - ` : ""}${option.name}` : ""}
                    getOptionSelected={(option, value) => option.idemployee === value.idemployee}
                    onChange={(event, value) =>
                      handleParameterChange({
                        target: {
                          name: "sprayer",
                          value: value ? value.name : "",
                        },
                      })
                    }
                    renderInput={(params) => <TextField {...params} placeholder="Search employee" />}
                    noOptionsText="No employees available"
                  />
                </FormGroup>
              </Grid>
            </>
          )}
        </Grid>

        <div style={{ marginTop: 8, marginBottom: 12 }}>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={handleAddCurrentProduct}
            disabled={!state.perimeter.product}
          >
            {editingAddedProductIndex !== null
              ? "[✓] Save Product Changes"
              : "[+] Add Product to Plan"}
          </Button>
          {editingAddedProductIndex !== null && (
            <Button
              variant="text"
              color="default"
              size="small"
              onClick={clearCurrentProductFields}
              style={{ marginLeft: 8 }}
            >
              {"Cancel Edit"}
            </Button>
          )}
        </div>

        {state.productsList.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <FormLabel>{"Products added"}</FormLabel>
            {state.productsList.map((item, index) => (
              <div
                key={`${item.idfinca_product}-${index}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  border: "1px solid #ddd",
                  borderRadius: 6,
                  padding: "6px 10px",
                  marginTop: 6,
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>
                    {item.product_name || "Product"}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "6px 12px",
                      fontSize: 13,
                      color: "#5c6b8a",
                    }}
                  >
                    {!containsDeland ? (
                      <span><strong>{containsOregon ? "Dose:" : "Dose/Acre:"}</strong> {item.dose || 0}</span>
                    ) : (
                      <span><strong>{"Dose/Acre:"}</strong> {item.dose_per_acre || 0}</span>
                    )}
                    {!containsDeland && (
                      <span><strong>{"Tanks:"}</strong> {item.tanks_applied || 0}</span>
                    )}
                    <span><strong>{"Unit Cost:"}</strong> {`$${format4(toNumber(item.unit_cost))}`}</span>
                    <span><strong>{"Total Cost:"}</strong> {`$${format4(toNumber(item.total_cost))}`}</span>
                    <span><strong>{"Cost/Acre:"}</strong> {`$${format4(toNumber(item.total_product_cost_per_acre))}`}</span>
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", marginLeft: 12 }}>
                  <Button
                    size="small"
                    color="primary"
                    onClick={() => handleEditAddedProduct(index)}
                  >
                    {"Edit"}
                  </Button>
                  <Button
                    size="small"
                    color="secondary"
                    onClick={() => removeAddedProduct(index)}
                    style={{ color: "#d32f2f" }}
                  >
                    {"Remove"}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {hideSections &&
          state.perimeter.area &&
          !canStore &&
          state.perimeter.missingMessage && (
            <div
              style={{
                color: "red",
                marginBottom: 12,
                fontWeight: "bold",
                fontSize: "1rem",
              }}
            >
              Cannot submit: {state.perimeter.missingMessage}
            </div>
        )}
        {cultivations &&
          state.perimeter.cultivation &&
          !canStore &&
          state.perimeter.missingMessage && (
            <div
              style={{
                color: "red",
                marginBottom: 12,
                fontWeight: "bold",
                fontSize: "1rem",
              }}
            >
              Cannot submit: {state.perimeter.missingMessage}
            </div>
        )}
        <div className={classes.buttonsContainer}>
          <Crud
            permissions={{
              create_attr: true,
              update_attr: true,
              delete_attr: true,
            }}
            create={{ create: clearForm, store }}
            update={update}
            destroy={destroy}
            cancel={clearForm}
            canStore={
              canStore &&
              state.productsList.length > 0 &&
              !hasPendingProductDraft()
            }
          />
        </div>
      </div>
    );
  }
);

export default PerimeterForm;
